import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, AlertTriangle, CalendarClock } from "lucide-react";
import BackButton from "../components/BackButton";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const FREQ = [
  { v: "annual", l: "Annuale" },
  { v: "semi_annual", l: "Semestrale" },
  { v: "quarterly", l: "Trimestrale" },
  { v: "monthly", l: "Mensile" },
];

function fmtDate(s) { if (!s) return "—"; try { return new Date(s).toLocaleDateString("it-IT"); } catch { return s; } }
function fmtEur(n) {
  if (n == null) return "—";
  return "€ " + Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function NewPolicyDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    provider: "", policy_number: "", coverage_type: "",
    start_date: "", end_date: "", premium_amount: "",
    premium_frequency: "annual", notes: "", active: true,
  });
  const [saving, setSaving] = useState(false);
  async function submit() {
    if (!form.provider.trim()) { toast.error("Compagnia obbligatoria"); return; }
    setSaving(true);
    try {
      const p = { ...form };
      Object.keys(p).forEach((k) => { if (p[k] === "" || p[k] == null) delete p[k]; });
      if (p.premium_amount != null) p.premium_amount = parseFloat(p.premium_amount);
      const created = await api.insuranceCreate(p);
      toast.success("Polizza creata");
      onCreated?.(created);
      onOpenChange(false);
      setForm({ provider: "", policy_number: "", coverage_type: "", start_date: "", end_date: "",
                 premium_amount: "", premium_frequency: "annual", notes: "", active: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="insurance-new-dialog">
        <DialogHeader><DialogTitle>Nuova polizza professionale</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Compagnia *</Label>
            <Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}
                   data-testid="ins-provider" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>N. polizza</Label>
              <Input value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} /></div>
            <div><Label>Tipo copertura</Label>
              <Input value={form.coverage_type} onChange={(e) => setForm({ ...form, coverage_type: e.target.value })}
                     placeholder="RC professionale" data-testid="ins-coverage" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data inizio</Label>
              <Input type="date" value={form.start_date}
                     onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label>Data fine</Label>
              <Input type="date" value={form.end_date}
                     onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                     data-testid="ins-end-date" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Premio (€)</Label>
              <Input type="number" step="0.01" value={form.premium_amount}
                     onChange={(e) => setForm({ ...form, premium_amount: e.target.value })} /></div>
            <div><Label>Frequenza</Label>
              <Select value={form.premium_frequency} onValueChange={(v) => setForm({ ...form, premium_frequency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{FREQ.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Note</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="ins-submit">
            {saving ? "…" : "Crea"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InsuranceList() {
  const [items, setItems] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [onlyActive, setOnlyActive] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [it, up] = await Promise.all([
        api.insuranceList({ active_only: onlyActive }),
        api.insuranceUpcoming({ days: 90 }),
      ]);
      setItems(it || []);
      setUpcoming(up || []);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [onlyActive]);

  async function del(id) {
    if (!window.confirm("Eliminare questa polizza?")) return;
    try {
      await api.insuranceDelete(id);
      toast.success("Eliminata");
      load();
    } catch { toast.error("Errore"); }
  }

  return (
    <div className="space-y-5" data-testid="insurance-page">
      <BackButton to="/" testid="insurance-back-btn" />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck size={22} className="opacity-70" />
            <h1 className="font-display text-3xl sm:text-4xl font-semibold">Assicurazioni professionali</h1>
          </div>
          <p className="mt-1 text-sm opacity-70">Polizze RC, tutela legale, ecc. · {items.length} totali</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch checked={onlyActive} onCheckedChange={setOnlyActive} data-testid="ins-only-active" />
            <Label className="text-xs">Solo attive</Label>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="insurance-new-btn">
            <Plus size={14} className="mr-1" /> Nuova polizza
          </Button>
        </div>
      </div>

      {upcoming.length > 0 && (
        <div className="pxos-card p-4 border-amber-200 bg-amber-50" data-testid="ins-upcoming">
          <div className="flex items-center gap-2 mb-2 text-amber-800">
            <AlertTriangle size={16} />
            <h3 className="font-semibold text-sm">In scadenza (90 gg)</h3>
          </div>
          <ul className="space-y-1 text-sm text-amber-800">
            {upcoming.map((p) => (
              <li key={p.id}>
                <strong>{p.provider}</strong> · {p.coverage_type || "—"} · scadenza {fmtDate(p.end_date)}
                {p.days_to_expiry != null && (
                  <span className="opacity-70"> ({p.days_to_expiry >= 0 ? `${p.days_to_expiry} gg` : `scaduta ${-p.days_to_expiry} gg fa`})</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="pxos-card p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center opacity-70">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center opacity-70" data-testid="insurance-empty">
            Nessuna polizza inserita.
          </div>
        ) : (
          <ul data-testid="insurance-list">
            {items.map((p) => (
              <li key={p.id} className="p-4 border-b flex flex-wrap items-center justify-between gap-2"
                  style={{ borderColor: "var(--pxos-border)" }}
                  data-testid={`insurance-item-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{p.provider}</div>
                  <div className="text-xs opacity-70 flex flex-wrap items-center gap-2 mt-0.5">
                    {p.coverage_type && <Badge variant="outline">{p.coverage_type}</Badge>}
                    {p.policy_number && <span>N. {p.policy_number}</span>}
                    {p.premium_amount && <span>· {fmtEur(p.premium_amount)} / {FREQ.find((f) => f.v === p.premium_frequency)?.l || p.premium_frequency}</span>}
                  </div>
                  <div className="text-xs opacity-70 mt-0.5">
                    <CalendarClock size={10} className="inline mr-1" />
                    {fmtDate(p.start_date)} → {fmtDate(p.end_date)}
                    {p.days_to_expiry != null && p.days_to_expiry >= 0 && p.days_to_expiry <= 90 && (
                      <span className="ml-2 text-amber-700 font-semibold">
                        · scade tra {p.days_to_expiry} gg
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {p.active ? (
                    <Badge className="bg-emerald-100 text-emerald-700">Attiva</Badge>
                  ) : (
                    <Badge variant="secondary">Cessata</Badge>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => del(p.id)}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <NewPolicyDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={() => load()} />
    </div>
  );
}
